interface ThemeColors {
  base: string;
  accent: string;
}

function hexToRgb(hex: string): [number, number, number] {
  const cleaned = hex.replace("#", "");
  return [
    parseInt(cleaned.slice(0, 2), 16),
    parseInt(cleaned.slice(2, 4), 16),
    parseInt(cleaned.slice(4, 6), 16),
  ];
}

function getLuminance(r: number, g: number, b: number): number {
  const linearize = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/** Returns the best readable foreground color (#FFFFFF or #1A1A1F) for a given background hex. */
export function getContrastColor(hex: string): "#FFFFFF" | "#1A1A1F" {
  const [r, g, b] = hexToRgb(hex);
  return getLuminance(r, g, b) > 0.179 ? "#1A1A1F" : "#FFFFFF";
}

/** Appends a 2-digit hex alpha to a 6-digit hex color. */
function withAlpha(hex: string, alpha: number): string {
  const a = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, "0");
  return `${hex}${a}`;
}

const luminanceOf = (hex: string) => getLuminance(...hexToRgb(hex));

/** WCAG contrast ratio between two hex colors (1:1 identical, 21:1 max). */
function contrastRatio(a: string, b: string): number {
  const first = luminanceOf(a);
  const second = luminanceOf(b);
  const high = Math.max(first, second);
  const low = Math.min(first, second);
  return (high + 0.05) / (low + 0.05);
}

/** WCAG 1.4.11 minimum for graphical objects such as a filled badge. */
const MIN_GRAPHIC_CONTRAST = 3;

/** The card background tag badges sit on outside the base-painted surfaces. */
const CARD_SURFACE = "#FFFFFF";

/**
 * Builds CSS custom property vars for an issuer theme.
 * Set these on the page root so all child components can consume them
 * without receiving color props individually.
 */
export function getThemeVars(
  theme: ThemeColors
): Record<string, string> & React.CSSProperties {
  const onBase = getContrastColor(theme.base);
  const onAccent = getContrastColor(theme.accent);

  // Tag fallback: whichever theme color reads best against a light card.
  // Accent alone is unusable here — it is #FFFFFF for most issuers, which is
  // invisible on white. Base wins for nearly all of them; issuers with a
  // near-white base (and therefore a dark accent) get the accent instead.
  const tagBg =
    contrastRatio(theme.base, CARD_SURFACE) >=
    contrastRatio(theme.accent, CARD_SURFACE)
      ? theme.base
      : theme.accent;
  const onTag = getContrastColor(tagBg);
  // A hairline edge only where the fill itself is too close to the card.
  const tagBorder =
    contrastRatio(tagBg, CARD_SURFACE) < MIN_GRAPHIC_CONTRAST
      ? withAlpha(onTag, 0.25)
      : "transparent";

  return {
    "--issuer-base": theme.base,
    "--issuer-accent": theme.accent,
    "--issuer-on-base": onBase,
    "--issuer-on-base-muted": withAlpha(onBase, 0.6),
    "--issuer-on-base-faint": withAlpha(onBase, 0.4),
    "--issuer-on-base-divider": withAlpha(onBase, 0.12),
    "--issuer-on-accent": onAccent,
    "--issuer-base-tint": withAlpha(theme.base, 0.1),
    "--issuer-base-tint-hover": withAlpha(theme.base, 0.18),
    "--issuer-tag-bg": tagBg,
    "--issuer-tag-fg": onTag,
    "--issuer-tag-border": tagBorder,
  } as React.CSSProperties;
}

/**
 * Normalizes a CMS-supplied color to a 6-digit hex, expanding the 3-digit form.
 * Returns undefined for anything unusable — `getContrastColor` assumes six hex
 * digits and would otherwise silently compute NaN luminance and pick white.
 */
export function normalizeHexColor(
  value: string | null | undefined
): string | undefined {
  if (!value) {
    return undefined;
  }
  const hex = value.trim().replace(/^#/, "");
  const expanded =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  return /^[0-9a-fA-F]{6}$/.test(expanded)
    ? `#${expanded.toLowerCase()}`
    : undefined;
}

/** Issuer theme fallbacks: secondary-100 on primary-100. Used for issuers whose
 *  CMS theme is missing or unparseable, and for cross-issuer pages such as the
 *  news feed, where no single issuer owns the surface. */
export const DEFAULT_THEME_BASE = "#404681";
export const DEFAULT_THEME_ACCENT = "#FFFFFF";
